/**
 * Arc Helpers
 *
 * Shared utilities for arc boundaries — used by both parent and arcs.
 */

import { assign } from "xstate";
import type { AnyStateMachine } from "xstate";

// ─── Arc discovery ───────────────────────────────────────────

/**
 * Static map of parent state names to their invoked child actor IDs.
 * Single source of truth — used by kiosk-renderer (event routing) and
 * kiosk-provider (refresh safety net).
 */
export const ARC_STATES: Record<string, string> = {
  triaging: "triageArc",
  returningPatient: "returningPatientArc",
  identifying: "identityArc",
  insuring: "insuranceArc",
  intaking: "intakeArc",
};

/**
 * Set of parent state names that invoke arc child machines.
 * Derived from ARC_STATES to stay in sync.
 */
export const ARC_PARENT_STATE_NAMES = new Set(Object.keys(ARC_STATES));

/**
 * Derives a map of { parentStateName → invokedActorId } directly from the
 * machine definition, so it stays in sync as new arcs are added without any
 * manual registration.
 */
export function buildArcStateToActorMap(
  machine: AnyStateMachine,
): Record<string, string> {
  // biome-ignore lint/suspicious/noExplicitAny: XState implementations is not narrowly typed
  const actorImpls = (machine.implementations as any).actors as
    | Record<string, unknown>
    | undefined;

  const result: Record<string, string> = {};
  for (const [stateName, stateNode] of Object.entries(machine.states)) {
    // biome-ignore lint/suspicious/noExplicitAny: XState StateNode.invoke is not narrowly typed
    const invoke = (stateNode as any).invoke as
      | Array<{ id: string; src?: unknown }>
      | undefined;
    if (invoke && invoke.length > 0) {
      // Iterate all invoke entries to find the first arc machine actor.
      // A state may invoke promise/callback actors alongside an arc machine.
      for (const entry of invoke) {
        if (!entry.id) continue;
        const srcKey = entry.src as string;
        const resolvedActor = actorImpls?.[srcKey];
        const isMachineActor =
          resolvedActor &&
          typeof resolvedActor === "object" &&
          "states" in resolvedActor;
        if (isMachineActor) {
          result[stateName] = entry.id;
          break;
        }
      }
    }
  }
  return result;
}

/**
 * Recursively collects state names from a state node, using dot notation
 * for compound substates (e.g. "ocrLicense.licenseFrontPhoto").
 * Skips "route" states and final states.
 */
function flattenArcStates(
  states: Record<string, unknown>,
  prefix = "",
): string[] {
  const result: string[] = [];
  for (const [name, node] of Object.entries(states)) {
    if (name === "route") continue;
    // biome-ignore lint/suspicious/noExplicitAny: XState StateNode typings
    const stateNode = node as any;
    if (stateNode.type === "final") continue;

    const fullName = prefix ? `${prefix}.${name}` : name;

    // Compound state: recurse into children instead of adding parent
    if (stateNode.states && Object.keys(stateNode.states).length > 0) {
      result.push(...flattenArcStates(stateNode.states, fullName));
    } else {
      result.push(fullName);
    }
  }
  return result;
}

/**
 * Returns arc state info for the JumpTab: which parent states invoke arcs,
 * what the arc's actor ID is, and what internal states the arc has.
 *
 * State names use dot notation for compound substates
 * (e.g. "ocrLicense.licenseFrontPhoto"). The JumpTab converts these to
 * nested XState values via `dotToStateValue()` when jumping.
 *
 * Returns: { parentStateName → { arcId, states: stateName[] } }
 */
export function getArcStatesForJump(
  machine: AnyStateMachine,
): Record<string, { arcId: string; states: string[] }> {
  // biome-ignore lint/suspicious/noExplicitAny: XState implementations is not narrowly typed
  const actorImpls = (machine.implementations as any).actors as
    | Record<string, unknown>
    | undefined;

  const result: Record<string, { arcId: string; states: string[] }> = {};
  for (const [stateName, stateNode] of Object.entries(machine.states)) {
    // biome-ignore lint/suspicious/noExplicitAny: XState StateNode.invoke is not narrowly typed
    const invoke = (stateNode as any).invoke as
      | Array<{ id: string; src?: unknown }>
      | undefined;
    if (invoke && invoke.length > 0) {
      for (const entry of invoke) {
        if (!entry.id) continue;
        const srcKey = entry.src as string;
        const resolvedActor = actorImpls?.[srcKey];
        const isMachineActor =
          resolvedActor &&
          typeof resolvedActor === "object" &&
          "states" in resolvedActor;
        if (isMachineActor) {
          // biome-ignore lint/suspicious/noExplicitAny: XState StateNode typings
          const arcStates = (resolvedActor as any).states as Record<
            string,
            unknown
          >;
          result[stateName] = {
            arcId: entry.id,
            states: flattenArcStates(arcStates),
          };
          break;
        }
      }
    }
  }
  return result;
}

/**
 * Converts a dot-notation state name to an XState state value.
 * - "workersCompQuestion" → "workersCompQuestion"
 * - "ocrLicense.licenseFrontPhoto" → { ocrLicense: "licenseFrontPhoto" }
 */
export function dotToStateValue(
  dotName: string,
): string | Record<string, unknown> {
  const parts = dotName.split(".");
  if (parts.length === 1) return dotName;
  // Build nested object from right to left
  let value: string | Record<string, unknown> = parts[parts.length - 1];
  for (let i = parts.length - 2; i >= 0; i--) {
    value = { [parts[i]]: value };
  }
  return value;
}

// ─── Date helpers ────────────────────────────────────────────

/** Strip time portion from ISO date string: "YYYY-MM-DDT..." → "YYYY-MM-DD". */
export const fromISODate = (d: string) => d.slice(0, 10);

/** Append UTC midnight to a date-only string: "YYYY-MM-DD" → "YYYY-MM-DDT00:00:00.000Z". */
export const toISODate = (d: string) => `${d.slice(0, 10)}T00:00:00.000Z`;

// ─── Arc-side helpers ────────────────────────────────────────

/**
 * Shared guard: has this arc been re-invoked with previousOutput?
 *
 * Every arc's `createXxxContext` sets `_reInvoked: true` when `previousOutput`
 * is present. This guard is used in the arc's `route` state to jump to the
 * exit-adjacent screen instead of starting over.
 *
 * Usage in arc setup:
 *   guards: { hasCompletedBefore: hasCompletedBefore }
 */
export const hasCompletedBefore = ({
  context,
}: { context: Record<string, unknown> }) => Boolean(context._reInvoked);

// ─── Parent-side helpers ─────────────────────────────────────

/**
 * Merge arc output into parent context on `onDone`.
 *
 * Spreads typed output fields into the parent context and caches
 * the full output under `cacheKey` for re-invocation (BACK navigation).
 *
 * The spread is transitional — non-extracted states still read flat fields
 * (e.g., `context.firstName`). Once all arcs are extracted, the parent context
 * becomes just session config + arc caches, and this helper stops spreading.
 *
 * Usage:
 *   onDone: {
 *     target: "nextState",
 *     actions: mergeArcOutput<IdentityOutput>("_identityOutput"),
 *   }
 */
export function mergeArcOutput<T extends Record<string, unknown>>(
  cacheKey: string,
  // biome-ignore lint/suspicious/noExplicitAny: XState's onDone action types are tightly coupled to machine actor/event generics
): any {
  return assign(({ event }: { event: { output: unknown } }) => {
    const output = event.output as T;
    return { ...output, [cacheKey]: output };
  });
}
